import {
  differenceInDays,
  JupiterTheme,
  parseISO,
} from '@jupiterone/jupiter-web-toolkit';
import { Button } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/styles';
import React from 'react';
import { useBooleanState } from 'react-use-object-state';

const styles = (theme: JupiterTheme) => {
  return createStyles({
    trialAlert: {
      color: theme.palette.red[800],
      marginLeft: 24,
      marginRight: 20,
      display: 'flex',
      flexFlow: 'column',
      alignItems: 'center',
      padding: '0 0 0 0',
      letterSpacing: 1.6,
      fontSize: 13,
      minWidth: '250px',
    },
    yellowButton: {
      backgroundColor: 'rgba(234, 252, 162, 0.15)',
      border: `1px solid rgba(234, 252, 162, 0.5)`,
      color: `rgba(234, 252, 162, 1)`,
      '&:hover, &:focus': {
        backgroundColor: 'rgba(200, 42, 82, 0.25)',
      },
      '&:active': {
        textDecoration: 'none',
      },
    },
    greenButton: {
      '&:active': {
        textDecoration: 'none',
      },
      '&:hover, &:focus': {
        border: `1px solid rgba(60, 227, 181, 1)`,
        backgroundColor: 'rgba(60, 227, 181, 0.25)',
        color: 'rgba(60, 227, 181, 1)',
        fontWeight: 900,
      },
    },
    whiteButton: {
      background: 'rgba(163, 170, 188, 0.1)',
      borderColor: 'rgba(163, 170, 188, 0.5)',
      color: theme.palette.coolGrey[300],
      '&:active': {
        textDecoration: 'none',
      },
      '&:hover, &:focus': {
        color: theme.palette.coolGrey[200],
        background: 'rgba(163, 170, 188, 0.25)',
        borderColor: theme.palette.coolGrey[200],
      },
    },
    trialButton: {
      fontSize: 14,
      fontWeight: 900,
      letterSpacing: -0.2,
      width: '100%',
    },
  });
};

interface Props extends WithStyles<typeof styles> {
  trialEndDate: string;
}

const TrialAlert = ({ trialEndDate, classes }: Props) => {
  const showUpgradeButton = useBooleanState(false);

  return (
    <div
      className={classes.trialAlert}
      onMouseOver={showUpgradeButton.setTrue}
      onMouseOut={showUpgradeButton.setFalse}
    >
      {showUpgradeButton.state ? (
        <Button
          className={classes.trialButton}
          classes={{ root: classes.greenButton }}
          onClick={() =>
            window.open('https://www.jupiterone.com/pricing', 'blank')
          }
          size="small"
        >
          Upgrade
        </Button>
      ) : (
        <Button
          className={classes.trialButton}
          size="small"
          classes={{
            root: getDaysLeft(trialEndDate)
              ? classes.yellowButton
              : classes.whiteButton,
          }}
        >
          {getDaysRemainingText(trialEndDate)}
        </Button>
      )}
    </div>
  );
};

function getDaysRemainingText(trialEndDate: string) {
  const daysLeft: number = getDaysLeft(trialEndDate);

  switch (daysLeft) {
    case -1:
      return `Trial Expired`;
    case 0:
      return `Your Trial Expires Today!`;
    case 1:
      return `Your Trial Expires Tomorrow!`;
    default:
      return `Your Trial Expires in ${daysLeft} Days`;
  }
}

function getDaysLeft(endDate: string) {
  const now = new Date();
  const daysLeft = differenceInDays(parseDate(endDate), now);

  return daysLeft < 0 ? -1 : daysLeft;
}

function parseDate(dateValue: string): number | Date {
  // TODO: Remove this if we know what the format is
  const date = Date.parse(dateValue);

  if (isNaN(date)) {
    return parseISO(dateValue);
  }

  return date;
}

export default withStyles(styles)(TrialAlert);
